import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewEncapsulation,
  ViewChild,
  ElementRef,
  AfterViewInit,
  inject,
  WritableSignal,
  signal,
  ChangeDetectorRef,
  NgZone,
  ViewRef,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { Subject, of } from 'rxjs';
import { catchError, takeUntil, distinctUntilChanged } from 'rxjs/operators';

import moment from 'moment';
import * as _ from 'lodash';
import { v4 as uuid4 } from 'uuid';

import { AuthService } from '../../services/auth.service';
import {
  onPlayerReady,
  onVideoPaused,
  onVideoPlay,
  onVideoPlayerErrorLoaded,
  onVideoPlayerInitialized,
  onVideoPlayerLoadedData,
  onCancelAddingAnnotation,
  onEditingAnnotation,
  onCommentsVisibilityChange,
  AnnotationService,
  onConversationPluginReady,
  onPluginReady,
} from '../../services/annotation.service';
import { HelperService } from '../../services/helper.service';
import { ProjectService } from '../../services/project.service';
import { SubsectionService } from '../../services/subsection.service';
import { MtmStompService, wsListenerANNOTATION_CREATED } from '../../services/mtm-stomp.service';
import { commentFileAdd } from '../../services/comments.service';
import { FileTransferService } from '../../services/signed-url/file-transfer.service';
import { MTMFileDownload } from '../../services/signed-url/mtm-file-download';
import { EmitterService } from '../../services/emitter.service';
import {
  AnnotationPrivateCondition,
  AiOptions,
  aiParamsFromAnnotationEvent,
  AnnotationSendMediaMessage,
} from '../../interfaces';

import { onDownloadStart } from '../mtm-file-download/mtm-file-download.service';
import { VideoAnnotationConfigService } from './video-annotation-config.service';
// import { CommentAnnotationPlugin } from '../comment-annotation/plugin/comment-annotation.plugin';
// import { CommentAnnotationPlugin } from "annotation";
import { CommentAnnotationPlugin, Environment, PluginConfig } from '@mtm/conversation-client-sdk-annotation';
import { VideoAnnotationHandler } from '../video-annotation-handler/video-annotation-handler';
import {
  VideoAnnotation,
  VideoFile,
  VideoItem,
  VideoOptions,
  ResolutionOption,
  TranscodeMetadata,
} from './video-annotation.interfaces';
import { TopMediaInfoService } from '../top-media-info/top-media-info.service';
import { HttpClient } from '@angular/common/http';
import {
  PlatformRbacSignatureService,
  SignatureRequestResult,
} from '../../services/platform-rbac-signature.service';
import { environment } from '../../../../environments/environment';
import videojs from 'video.js';
import { FileTypeService } from '../../services/file-type.service';
import { FileUtilsService } from '../../services/file-utils.service';
import { ProjectFileService } from '../../services/project-file.service';
import { MediaPlayerStoreService } from '../../services/media-player-store.service';
import { OverlayContainer } from '@angular/cdk/overlay';
import { currentAppToken } from '../../auth/app-token';

@Component({
  selector: 'mtm-video-annotation',
  templateUrl: './video-annotation.component.html',
  styleUrl: './video-annotation.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class VideoAnnotationComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private readonly httpClient = inject(HttpClient);
  private readonly rbacSignature = inject(PlatformRbacSignatureService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly mpStoreService = inject(MediaPlayerStoreService);
  private readonly overlayContainer = inject(OverlayContainer);

  /** When the control bar is narrower than this (px), overflow actions go into the "more" menu. */
  readonly controlBarMoreDropdownBreakpointPx = 600;

  controlBarIsNarrow = false;

  private controlBarResizeObserver: ResizeObserver | null = null;

  private injectRetryTimeoutId?: number;

  @ViewChild('videoPlayer') videoPlayer!: ElementRef;

  // Custom tool buttons to inject into video.js
  @ViewChild('playButtonWrapper') playButtonWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('volumeButtonWrapper') volumeButtonWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('timeDisplayWrapper') timeDisplayWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('settingsButtonWrapper') settingsButtonWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('cinemaModeButtonWrapper') cinemaModeButtonWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('rejectButtonWrapper') rejectButtonWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('approveButtonWrapper') approveButtonWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('compareButtonWrapper') compareButtonWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('infoButtonWrapper') infoButtonWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('annotationToggleButtonWrapper') annotationToggleButtonWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('fullscreenButtonWrapper') fullscreenButtonWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('downloadButtonWrapper') downloadButtonWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('moreButtonWrapper') moreButtonWrapper?: ElementRef<HTMLDivElement>;

  // Inputs — video-viewer uses store-backed + direct groups below; legacy group is for old mtm-media-player / not new UI.

  // From store signals (video-viewer: projectIdSignal, sectionIdSignal, subsectionIdSignal, typeIdSignal,
  // enableAnnotationSignal, annotationsSignal, hideAnnotationToolbarSignal, projectParticipantsSignal,
  // portfolioPreviewSignal, videoPreloadMethodSignal, itemSignal for item + file, isShareLinkPreviewSignal)
  @Input() projectId!: string;
  @Input() sectionId!: string;
  @Input() subsectionId!: string;
  @Input() typeId!: string;
  @Input() enableVideoAnnotation = false;
  @Input() annotations: VideoAnnotation[] = [];
  @Input() hideAnnotationToolbar!: string;
  @Input() projectParticipants: any[] = [];
  @Input() portfolioPreview = false;
  @Input() videoPreloadMethod = 'metadata';
  @Input() item!: VideoItem;
  @Input() file!: VideoFile;
  @Input() shareLinkPreview: boolean = false;

  // Direct @Input (video-viewer)
  @Input() autoPlayVideo = false;
  @Input() height!: string;
  @Input() width!: string;
  @Input() miniPlayer = false;
  @Input() instanceId!: string;

  // Legacy / not used on new comment UI — old mtm-media-player or non-video-viewer paths
  @Input() canAddAnnotationComment = false;
  @Input() previewSubtitle = false;
  @Input() customPaddingTop!: string;
  @Input() enableSubtitle = false;
  @Input() subtitle: any;
  @Input() videoSubtitle: any;
  @Input() directPreview = false;

  // Outputs - Bound to video-viewer
  @Output() videoPlayerLoaded = new EventEmitter<void>();

  // Outputs — not bound from video-viewer; legacy parent hooks (player ready, download, thumbnails, init, fullscreen).
  @Output() videoPlayerReady = new EventEmitter<void>();
  @Output() videoDownload = new EventEmitter<{ url: string; quality?: string }>();
  @Output() videoThumbnailLoad = new EventEmitter<void>();
  @Output() videoInitializationStarted = new EventEmitter<void>();
  @Output() playerOnFullscreenChange = new EventEmitter<void>();
  @Output() videoThumbnailLoadError = new EventEmitter<void>();

  // Two-way pairs — old mtm-media-player only; video-viewer keeps this state inside the component.
  @Input() isBusy = false;
  @Output() isBusyChange = new EventEmitter<boolean>();
  updateIsBusy = (value: boolean): void => this.setAndEmit('isBusy', this.isBusyChange, value);

  @Input() isError = false;
  @Output() isErrorChange = new EventEmitter<boolean>();
  updateIsError = (value: boolean): void => this.setAndEmit('isError', this.isErrorChange, value);

  @Input() isPlayerReady = false;
  @Output() isPlayerReadyChange = new EventEmitter<boolean>();
  updatePlayerReady = (value: boolean): void => this.setAndEmit('isPlayerReady', this.isPlayerReadyChange, value);

  @Input() videoOptions!: VideoOptions;
  @Output() videoOptionsChange = new EventEmitter<VideoOptions>();
  updateVideoOptions = (value: VideoOptions): void => this.setAndEmit('videoOptions', this.videoOptionsChange, value);

  @Input() oldItem = false;
  @Output() oldItemChange = new EventEmitter<boolean>();
  updateOldItem = (value: boolean): void => this.setAndEmit('oldItem', this.oldItemChange, value);

  @Input() metadataCheckTimeout: any;
  @Output() metadataCheckTimeoutChange = new EventEmitter<any>();
  updateMetadataCheckTimeout = (value: any): void =>
    this.setAndEmit('metadataCheckTimeout', this.metadataCheckTimeoutChange, value);

  @Input() player: any;
  @Output() playerChange = new EventEmitter<any>();
  updatePlayer = (value: any): void => this.setAndEmit('player', this.playerChange, value);

  private setAndEmit<T>(prop: keyof VideoAnnotationComponent, emitter: EventEmitter<T>, value: T): void {
    (this as any)[prop] = value;
    emitter.emit(value);
  }

  annotationPlugin: CommentAnnotationPlugin | null = null;
  // config: { userInfo: any; item: VideoItem; annotationObjects: VideoAnnotation[]; instanceId: string; itemSignature: any; httpClient: any; participants: any[] } | null = null;
  config: any;
  pluginReady = false;
  videoHandler: VideoAnnotationHandler | null = null;
  annotationComment: string = '';
  isAnnotationModeActive = false;
  wsId: string | null = null;
  newFiles: any[] = [];
  privateCondition: AnnotationPrivateCondition | null = null;
  editingAnnotation: any = null;
  isReleasePreview = false;
  isShareLinkPreview = false;
  signature: any;
  authUser: any;

  currentResolution: WritableSignal<string> = signal<string>('720');
  currentPlaybackRate: WritableSignal<number> = signal<number>(1);
  progressBarClickedTime: number | null = null;
  cinemaMode = false;
  isVideoFullscreen = false;
  isPlaying = false;
  isMuted = false;
  currentVolume = 1;

  get isFullscreen(): boolean {
    return this.isVideoFullscreen;
  }

  currentTime = 0;
  duration = 0;
  frameRate = 25;
  timeDisplayFormat: 'Seconds' | 'Frames' = 'Seconds';

  get isAdding(): boolean {
    return this.annotationPlugin?.controls?.uiState?.adding && this.annotationPlugin?.controls?.uiState?.drawingEnabled;
  }

  get annotatingMode(): boolean {
    return this.isAdding || false;
  }

  get editingMode(): boolean {
    return this.annotationPlugin?.controls?.uiState?.editing || false;
  }

  private readonly ngUnsubscribe = new Subject<void>();
  private readonly TARGET_DATE = moment('2025-02-01').valueOf();
  private isVideoSourceSet = false;
  private videoTypeCheckInterval: any;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly authService: AuthService,
    private readonly transferService: FileTransferService,
    private readonly projectService: ProjectService,
    private readonly stompService: MtmStompService,
    private readonly annotationService: AnnotationService,
    private readonly subsectionService: SubsectionService,
    private readonly configService: VideoAnnotationConfigService,
    private readonly topMediaInfoService: TopMediaInfoService,
    private readonly fileTypeService: FileTypeService,
    private readonly fileUtilsService: FileUtilsService,
    private readonly helperService: HelperService,
    private readonly projectFileService: ProjectFileService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['videoSubtitle']) {
      this.setVideoSubtitles();
    }

    if (changes['annotations']) {
      if (!changes['annotations'].isFirstChange() && this.annotationPlugin && this.pluginReady && this.annotations) {
        setTimeout(() => {
          this.videoHandler?.buildIntervalTree();
          this.videoHandler?.updateCanvasPreview();
        }, 0);
      }
    }
  }

  ngOnInit(): void {
    this.authUser = this.authService.getAuthUser();
    this.checkValidOriginal();

    this.annotationService.privateConditionChange$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (value: AnnotationPrivateCondition) => {
        this.privateCondition = value;
      },
    });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.isShareLinkPreview === curr.isShareLinkPreview)
      )
      .subscribe({
        next: (state) => {
          this.isShareLinkPreview = state.isShareLinkPreview;
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.isReleasePreview === curr.isReleasePreview)
      )
      .subscribe({
        next: (state) => {
          this.isReleasePreview = state.isReleasePreview;
        },
      });

    EmitterService.get(AnnotationSendMediaMessage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data) => {
          if (this.instanceId != data.instanceId) {
            return;
          }
          if (data.message) {
            this.annotationComment = data.message;
            this.newFiles = data.newFiles || [];
            if (data.type === 'add') {
              this.saveAnnotation(aiParamsFromAnnotationEvent(data));
            } else if (data.type === 'edit') {
              if (this.editingMode) {
                this.saveEditedAnnotation(aiParamsFromAnnotationEvent(data));
              } else {
                this.annotationPlugin.fire('updateComment', {
                  annotationId: data.comment.annotation.id,
                  commentId: data.comment.comment.id,
                  body: data.message,
                  files: data.newFiles,
                  agentVersionId: data.agentVersionId,
                  mediaQuality: data.mediaQuality,
                });
              }
            }

            // this.editingAnnotation = null;
            this.isBusy = false;
          }
        },
      });
  }

  private isCurrentTarget(args: any): boolean {
    return args?.instanceId === this.instanceId && args?.itemId === this.item?.id;
  }

  onCancelAddingAnnotationHandler(args: any) {
    if (!this.isCurrentTarget(args)) {
      return;
    }
    this.resetUIComponent();
  }

  updateMarker(args: any) {
    if (!this.isCurrentTarget(args)) {
      return;
    }

    // if (args.itemId == this.item.id) {
    //   this.annotationPlugin.annotationState.annotations = this.annotations;
    // }
  }

  wsOnAnnotationCreate(args: any) {
    commentFileAdd.emit({
      projectId: this.projectId,
      sectionId: this.sectionId,
      subSectionId: this.subsectionId,
      comment: args.mediaAnnotation.comments[0],
      annotationId: args.mediaAnnotation.id,
      files: this.newFiles,
    });
    this.cleanUpFiles();
  }

  saveAnnotation(aiParams: AiOptions) {
    if ((!this.annotationComment && !this.newFiles?.length) || this.isBusy) {
      return;
    }
    this.isBusy = true;
    if (this.newFiles.length && !this.annotationComment) {
      this.annotationComment = ' ';
    }
    if (!this.editingMode) {
      this.addNewAnnotation(aiParams);
    } else if (!this.annotatingMode && this.editingMode) {
      this.saveEditedAnnotation(aiParams);
    }
  }

  saveEditedAnnotation(aiParams: AiOptions) {
    if (this.annotationPlugin && this.editingMode && this.pluginReady) {
      this.videoHandler.saveEditedAnnotation(this.annotationComment, aiParams);
    }
  }

  addNewAnnotation(aiParams: AiOptions) {
    this.annotationService.updateCommentListVisibility(this.privateCondition);
    if (this.annotationPlugin && this.annotatingMode && this.pluginReady) {
      this.videoHandler.saveAnnotation(this.annotationComment, aiParams, false);
    } else if (this.annotationPlugin && this.pluginReady && !this.annotatingMode && !this.editingMode) {
      this.videoHandler.saveAnnotation(this.annotationComment, aiParams, true);
    }
  }

  resetUIComponent() {
    this.annotationComment = '';
    this.isBusy = false;
  }

  cleanUpFiles() {
    this.newFiles = [];
  }

  ngAfterViewInit(): void {
    this.wsId = this.stompService.subscribeToService();

    onCancelAddingAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onCancelAddingAnnotationHandler(args),
    });

    // onEditingAnnotation.pipe(
    //   takeUntil(this.ngUnsubscribe)
    // ).subscribe({
    //   next: (args: any) => this.editAnnotation(args)
    // });

    onCommentsVisibilityChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.updateMarker(args),
    });

    this.stompService.subscribeToListener(
      'ANNOTATION_CREATED',
      this.wsId,
      [this.projectId, this.sectionId, this.subsectionId],
      (args: any) => this.wsOnAnnotationCreate(args),
      wsListenerANNOTATION_CREATED
    );
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.injectRetryTimeoutId);
    if (this.controlBarResizeObserver) {
      this.controlBarResizeObserver.disconnect();
      this.controlBarResizeObserver = null;
    }
    if (this.videoHandler) {
      this.videoHandler.destroy();
      this.videoHandler = null;
    }

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();

    if (this.wsId) {
      this.stompService.unsubscribeToService(this.wsId);
    }
  }

  private checkValidOriginal(): void {
    setTimeout(() => {
      const thumbnailUrl = this.file.signedURL + (this.directPreview ? '' : '?thumbnail=_SMALL');
      const img = new Image();

      img.onload = () => {
        this.videoThumbnailLoad.emit();
        this.initializeVideoJs();
      };

      img.onerror = () => {
        this.handleThumbnailError();
      };

      img.src = thumbnailUrl;
    }, 100);
  }

  private handleThumbnailError(): void {
    if (this.file?.name && this.checkMp4(this.file.name)) {
      this.videoThumbnailLoadError.emit();
      this.initializeVideoJs();
    } else {
      this.videoThumbnailLoadError.emit();
      this.retryThumbnailLoad();
    }
  }

  private retryThumbnailLoad(): void {
    if (this.file?.signedURL) {
      fetch(this.file.signedURL + '?thumbnail=_TRANSCODED', {
        headers: {
          Authorization: `Bearer ${currentAppToken()}`,
        },
      });
    }
    this.videoTypeCheckInterval = setTimeout(this.checkValidOriginal.bind(this), 5000);
  }

  private getVideoType(): string {
    return this.fileTypeService.getVideoMimeType(this.file);
  }

  private isVideoExtension(file: VideoFile, extensions: string[]): boolean {
    return extensions.some(
      (ext) =>
        (file.publicLink && this.isFileExtension(file.publicLink, ext)) ||
        (file.name && this.isFileExtension(file.name, ext))
    );
  }

  private isFileExtension(filename: string, extension: string): boolean {
    return this.fileUtilsService.isFileExtension(filename, extension);
  }

  private setVideoSource(useTranscoded = false): void {
    const source = this.sanitizer.bypassSecurityTrustUrl(this.file.signedURL) as any;
    const sourceSafe = source.changingThisBreaksApplicationSecurity;
    const type = this.getVideoType();

    const transcodedVideos =
      this.file?.transcodeMetadata?.filter((item) => item.codec !== 'jpeg' && item.codec !== 'jpg') || [];

    const resolutionArray = this.buildResolutionArray(transcodedVideos, sourceSafe, type);

    if (resolutionArray.length > 0 && useTranscoded) {
      this.player.updateSrc(resolutionArray);
    } else {
      this.setDefaultVideoSource(sourceSafe, type);
    }
  }

  private buildResolutionArray(
    transcodedVideos: TranscodeMetadata[],
    sourceSafe: string,
    type: string
  ): ResolutionOption[] {
    return transcodedVideos
      .map((video) => {
        const resolution = video.quality.substr(1);
        if (resolution !== 'SMALL' && resolution !== 'TINY') {
          return {
            src: sourceSafe + (this.directPreview ? '' : `?thumbnail=_${resolution}`),
            type,
            label: `${resolution}p`,
            res: resolution,
          };
        }
        return null;
      })
      .filter(Boolean) as ResolutionOption[];
  }

  private setDefaultVideoSource(sourceSafe: string, type: string): void {
    const sourceOptions = {
      src: sourceSafe + (this.directPreview ? '' : '?thumbnail=_TRANSCODED'),
      type,
      label: 'Source',
    };

    if (!this.isVideoSourceSet) {
      this.player.updateSrc([sourceOptions]);
      this.isVideoSourceSet = true;
    }
  }

  private hasCommentsBeforeDate(): boolean {
    if (!this.annotations?.length) {
      return false;
    }

    return this.annotations.some((annotation) => {
      if (!annotation.comments?.length) {
        return false;
      }

      return annotation.comments.some((comment) => {
        const commentDate = comment.meta?.datetime;
        return commentDate && moment(commentDate).valueOf() < this.TARGET_DATE;
      });
    });
  }

  private setVideoAnnotationConfig(): void {
    this.item.metadata = this.item?.metadata || '[]';
    this.item.participants = this.item?.participants || this.projectParticipants;

    const user = this.authService.getAuthUser();

    this.config = {
      userInfo: user,
      item: this.item,
      entityName: 'FILE',
      annotationObjects: this.annotations,
      instanceId: this.instanceId,
      itemSignature: this.signature.access_token,
      httpClient: this.httpClient,
      participants: this.prepareParticipantList(),
      environment: environment.environmentType as Environment,
      ...(this.shareLinkPreview ? { isPrivate: false } : {}),
    };

    this.annotationPlugin = new CommentAnnotationPlugin(this.config);

    this.annotationPlugin.onReady(() => {
      this.videoHandler = new VideoAnnotationHandler(this.player, this.annotationPlugin, !!this.oldItem);
      this.pluginReady = true;
      onPluginReady.emit({
        annotationPlugin: this.annotationPlugin,
        itemId: this.item.id,
        instanceId: this.instanceId,
        type: 'video',
      });
      onConversationPluginReady.emit({
        annotationPlugin: this.annotationPlugin,
        instanceId: this.instanceId,
        itemId: this.item?.id,
      });
    });
  }

  private initializeVideoJs(): void {
    this.videoInitializationStarted.emit();

    const checkAndInitialize = () => {
      if (!this.file?.metadata || JSON.parse(this.file.metadata).length === 0) {
        this.fetchAndInitializeFile();
        return;
      }

      this.initializePlayer();
    };

    checkAndInitialize();
  }

  private fetchAndInitializeFile(): void {
    const projectId = this.projectId ?? this.projectService.project?.id;

    this.projectFileService.getFileInfo(projectId, this.file.id).subscribe({
      next: (data) => {
        this.fileUtilsService.copyFilePropsAfterScanning(data, this.file);

        if (!this.file?.metadata || JSON.parse(this.file.metadata).length === 0) {
          this.updateIsError(true);
          this.updateMetadataCheckTimeout(setTimeout(this.initializeVideoJs.bind(this), 5000));
          return;
        }

        this.updateIsError(false);
        this.initializePlayer();
      },
      error: (err) => {
        this.updateMetadataCheckTimeout(setTimeout(this.initializeVideoJs.bind(this), 5000));
      },
    });
  }

  private savePlayerToStore(): void {
    if (!this.instanceId || !this.player) return;
    const store = this.mpStoreService.getStore(this.instanceId);
    store?.updateStore({ player: this.player });
  }

  private initializePlayer(): void {
    if (this.enableVideoAnnotation) {
      this.initializeAnnotationPlayer();
    } else {
      this.initializeBasicPlayer();
    }
  }

  private initializeAnnotationPlayer(): void {
    if (this.isBusy) return;

    this.updateIsBusy(true);

    setTimeout(() => {
      if (this.videoPlayer) {
        videojs.resetFormatTime();
        this.setDefaultVideoOptions();

        this.player = videojs(this.videoPlayer.nativeElement, this.videoOptions, () => {
          this.savePlayerToStore();
          this.setVideoSource(true);
          //this.setVideoVRProjection();
          this.updateOldItem(this.hasCommentsBeforeDate());
          this.generateSignature().subscribe((result) => {
            this.signature = result?.signature ?? null;
            this.setVideoAnnotationConfig();
          });

          this.setupPlayerEvents();
          this.setupPlayerReadyEvent();
        });
      } else {
        this.updateIsBusy(false);
      }
    }, 0);
  }

  private initializeBasicPlayer(): void {
    setTimeout(() => {
      if (this.videoPlayer) {
        this.setDefaultVideoOptions();

        this.player = videojs(this.videoPlayer.nativeElement, this.videoOptions, () => {
          this.savePlayerToStore();
          this.setVideoSource(true);
          //this.setVideoVRProjection();

          this.setupBasicPlayerEvents();
          this.setupPlayerReadyEvent();
          this.setVideoSubtitles();

          this.isPlayerReady = true;
          this.updatePlayerReady(this.isPlayerReady);
        });
      }

      this.updateIsBusy(false);
    }, 0);
  }

  private setupPlayerEvents(): void {
    this.player.on('loadeddata', () => {
      this.player.hasStarted(true);

      onVideoPlayerLoadedData.emit({
        act: 'onVideoPlayerLoadedData',
        player: this.player,
        annotationPlugin: this.annotationPlugin,
        projectId: this.projectId,
        sectionId: this.sectionId,
        itemId: this.item.id,
        instanceId: this.instanceId,
      });

      this.videoPlayerLoaded.emit();
    });

    this.player.on('fullscreenchange', () => {
      this.handleFullscreenChange();
      this.ngZone.run(() => {
        this.isVideoFullscreen = this.player.isFullscreen();
        this.topMediaInfoService.updateState({ isFullscreen: this.isVideoFullscreen });
        this.playerOnFullscreenChange.emit();
      });
    });

    this.player.on('pause', () => {
      this.emitVideoEvent('onVideoPaused');
      this.ngZone.run(() => {
        this.isPlaying = false;
      });
    });

    this.player.on('play', () => {
      this.emitVideoEvent('onVideoPlay');
      this.ngZone.run(() => {
        this.isPlaying = true;
      });
    });

    this.player.on('volumechange', () => {
      this.ngZone.run(() => {
        this.isMuted = this.player.muted();
        this.currentVolume = this.player.volume();
      });
    });

    this.player.on('timeupdate', () => {
      this.ngZone.run(() => {
        this.currentTime = this.player.currentTime();
      });
    });

    this.player.on('durationchange', () => {
      this.ngZone.run(() => {
        this.duration = this.player.duration();
      });
    });

    this.player.on('error', () => {
      onVideoPlayerErrorLoaded.emit();
      if (!this.isVideoSourceSet) {
        this.setVideoSource(false);
      }
    });

    this.player.on('seeked', () => {
      if (this.enableVideoAnnotation && this.player) {
        const time = this.player.currentTime();
        if (typeof time === 'number' && !Number.isNaN(time)) {
          this.progressBarClickedTime = time;
          setTimeout(() => (this.progressBarClickedTime = null), 0);
        }
      }
    });

    this.emitVideoEvent('videoPlayerInitialized');
    this.player.el().classList.add('vjs-has-started');
    this.updateIsBusy(false);
  }

  private setupBasicPlayerEvents(): void {
    this.player.on('loadeddata', () => {
      if (this.portfolioPreview) {
        $(this.player.controlBar.resolutionSwitcher).hide();
      }
      this.player.currentTime(0);
    });

    this.player.on('fullscreenchange', () => {
      this.syncOverlayContainerToFullscreen();
      this.syncMarkerContainerToFullscreen();
      this.ngZone.run(() => {
        this.isVideoFullscreen = this.player.isFullscreen();
        this.topMediaInfoService.updateState({ isFullscreen: this.isVideoFullscreen });
        this.playerOnFullscreenChange.emit();
      });
    });

    this.player.on('play', () => {
      this.ngZone.run(() => {
        this.isPlaying = true;
      });
    });

    this.player.on('pause', () => {
      this.ngZone.run(() => {
        this.isPlaying = false;
      });
    });

    this.player.on('volumechange', () => {
      this.ngZone.run(() => {
        this.isMuted = this.player.muted();
        this.currentVolume = this.player.volume();
      });
    });

    this.player.on('timeupdate', () => {
      this.ngZone.run(() => {
        this.currentTime = this.player.currentTime();
      });
    });

    this.player.on('durationchange', () => {
      this.ngZone.run(() => {
        this.duration = this.player.duration();
      });
    });

    this.player.on('error', () => {
      onVideoPlayerErrorLoaded.emit();
      if (!this.isVideoSourceSet) {
        this.setVideoSource(false);
      }
    });
  }

  private setupPlayerReadyEvent(): void {
    this.player.on('ready', () => {
      this.setupDocumentClickHandlers();
      this.setCustomPadding();
      this.emitPlayerReadyEvents();
    });
  }

  private setupDocumentClickHandlers(): void {
    $(document).on('click', (e) => {
      if (!$(e.target).hasClass('vjs-vjsdownload')) {
        $('.vjs-vjsdownload.vjs-control.vjs-button').removeClass('vjs-menu-open');
      }
      if (!$(e.target).hasClass('vjs-playback-rate')) {
        $('.vjs-playback-rate').removeClass('vjs-menu-open');
      }
      if ($(e.target).parent('.vjs-resolution-button').length === 0) {
        $('.vjs-resolution-button').removeClass('vjs-menu-open');
      }
    });
  }

  private setCustomPadding(): void {
    if (this.customPaddingTop) {
      this.player.el().style.setProperty('padding-top', this.customPaddingTop, 'important');
    }
  }

  private emitPlayerReadyEvents(): void {
    this.videoPlayerReady.emit();

    onVideoPlayerInitialized.emit({
      act: 'videoPlayerInitialized',
    });

    onPlayerReady.emit({
      act: 'onPlayerReady',
      player: this.player,
      annotationPlugin: this.annotationPlugin,
      projectId: this.projectId,
      sectionId: this.sectionId,
      subSectionId: this.subsectionId,
      typeId: this.typeId,
      itemId: this.item.id,
      metadata: this.item?.metadata,
      instanceId: this.instanceId,
    });

    this.isPlayerReady = true;
    this.updatePlayerReady(this.isPlayerReady);
    this.updatePlayer(this.player);

    if ((this.cdr as ViewRef).destroyed) return;
    // Materialize the @if (isPlayerReady) block now so the wrapper ViewChild refs exist
    // before injection — a bare timeout loses this race on busy first page loads.
    this.cdr.detectChanges();
    this.injectControlBarWhenReady();
  }

  private getCustomButtonWrappers(): (ElementRef<HTMLDivElement> | undefined)[] {
    return [
      this.playButtonWrapper,
      this.volumeButtonWrapper,
      this.timeDisplayWrapper,
      this.settingsButtonWrapper,
      this.cinemaModeButtonWrapper,
      this.rejectButtonWrapper,
      this.approveButtonWrapper,
      this.compareButtonWrapper,
      this.downloadButtonWrapper,
      this.infoButtonWrapper,
      this.annotationToggleButtonWrapper,
      this.fullscreenButtonWrapper,
      this.moreButtonWrapper,
    ];
  }

  private injectControlBarWhenReady(attempt = 0): void {
    if ((this.cdr as ViewRef).destroyed || this.player?.isDisposed?.()) return;

    const wrappers = this.getCustomButtonWrappers();
    const ready = wrappers.every((wrapper) => wrapper?.nativeElement) && !!this.player?.controlBar?.el?.();

    if (!ready && attempt < 3) {
      this.injectRetryTimeoutId = window.setTimeout(() => this.injectControlBarWhenReady(attempt + 1), 100);
      return;
    }

    wrappers.forEach((wrapper) => wrapper?.nativeElement?.classList.remove('vjs-hidden-until-injected'));
    if (!ready) return;

    this.injectCustomControlBarButtons(this.player);
    this.setupProgressBarTooltipFormat();
  }

  private applyControlBarOverflowClass(): void {
    const barEl = this.player?.controlBar?.el() as HTMLElement | undefined;
    if (!barEl) return;
    barEl.classList.toggle('vjs-control-bar-overflow-menu', this.controlBarIsNarrow);
  }

  private setupControlBarWidthObserver(barEl: HTMLElement): void {
    if (this.controlBarResizeObserver) {
      this.controlBarResizeObserver.disconnect();
      this.controlBarResizeObserver = null;
    }
    const applyWidth = (): void => {
      const w = barEl.getBoundingClientRect().width;
      const narrow = w > 0 && w < this.controlBarMoreDropdownBreakpointPx;
      this.ngZone.run(() => {
        if (this.controlBarIsNarrow !== narrow) {
          this.controlBarIsNarrow = narrow;
          this.applyControlBarOverflowClass();
        }
      });
    };
    this.controlBarResizeObserver = new ResizeObserver(() => {
      applyWidth();
    });
    this.controlBarResizeObserver.observe(barEl);
    applyWidth();
  }

  private injectCustomControlBarButtons(player: any): void {
    try {
      if (!player?.controlBar?.el) return;
      const barEl: HTMLElement = player.controlBar.el();
      if (!barEl) return;

      const fullscreenToggle = barEl.querySelector('.vjs-fullscreen-control');
      [
        '.vjs-play-control',
        '.vjs-volume-panel',
        '.vjs-resolution-button',
        '.vjs-picture-in-picture-control',
        '.vjs-vjsdownload',
        '.vjs-fullscreen-control',
        '.vjs-playback-rate',
        '.vjs-time-control',
        '.vjs-time-divider',
        '.vjs-duration',
      ].forEach((selector) => {
        const toggle = barEl.querySelector(selector);
        if (toggle) {
          (toggle as HTMLElement).style.display = 'none';
        }
      });

      const elementsToInject = [
        this.rejectButtonWrapper,
        this.approveButtonWrapper,
        this.compareButtonWrapper,
        this.downloadButtonWrapper,
        this.infoButtonWrapper,
        this.annotationToggleButtonWrapper,
        this.settingsButtonWrapper,
        this.cinemaModeButtonWrapper,
        this.fullscreenButtonWrapper,
        this.moreButtonWrapper,
      ];

      elementsToInject.forEach((wrapper) => {
        if (wrapper?.nativeElement) {
          if (fullscreenToggle?.parentNode === barEl) {
            barEl.insertBefore(wrapper.nativeElement, fullscreenToggle);
          } else {
            barEl.appendChild(wrapper.nativeElement);
          }
        }
      });

      // Explicitly prepend play and volume items at index 0 and 1 of the control bar
      if (this.timeDisplayWrapper?.nativeElement) {
        barEl.insertBefore(this.timeDisplayWrapper.nativeElement, barEl.firstChild);
      }
      if (this.volumeButtonWrapper?.nativeElement) {
        barEl.insertBefore(this.volumeButtonWrapper.nativeElement, barEl.firstChild);

        // Move the native volume panel DOM node INSIDE the volume wrapper
        // so CSS :hover on the wrapper can show/hide the slider
        const volPanel = barEl.querySelector('.vjs-volume-panel') as HTMLElement;
        if (volPanel) {
          this.volumeButtonWrapper.nativeElement.appendChild(volPanel);
        }
      }
      if (this.playButtonWrapper?.nativeElement) {
        barEl.insertBefore(this.playButtonWrapper.nativeElement, barEl.firstChild);
      }

      this.setupControlBarWidthObserver(barEl);
    } catch {
      // no-op
    }
  }
  private handleFullscreenChange(): void {
    const customSpacer = this.player.controlBar.getChild('customControlSpacer').el();
    $(customSpacer).css('min-width', '');

    setTimeout(() => {
      $(customSpacer).css('min-width', customSpacer.offsetWidth);
    }, 500);

    this.syncOverlayContainerToFullscreen();
    this.syncMarkerContainerToFullscreen();
  }

  public onCinemaModeExitFullscreen(): void {
    if (this.player.isFullscreen()) {
      this.player.exitFullscreen();
    }
  }

  private syncOverlayContainerToFullscreen(): void {
    const overlayContainer: HTMLElement = this.overlayContainer.getContainerElement();
    if (this.player.isFullscreen()) {
      this.player.el().appendChild(overlayContainer);
    } else {
      document.body.appendChild(overlayContainer);
    }
  }

  private syncMarkerContainerToFullscreen(): void {
    const playerEl = this.player.el() as HTMLElement;
    if (this.player.isFullscreen()) {
      const markerContainer = playerEl.closest('.video-annotation-container')
        ?.querySelector('.marker-container') as HTMLElement | null;
      if (markerContainer) {
        const controlBar = playerEl.querySelector('.vjs-control-bar') as HTMLElement | null;
        (controlBar ?? playerEl).appendChild(markerContainer);
      }
    } else {
      const markerContainer = playerEl.querySelector('.marker-container') as HTMLElement | null;
      if (markerContainer) {
        playerEl.parentElement?.appendChild(markerContainer);
      }
    }
  }

  private emitVideoEvent(eventType: string): void {
    const eventData = {
      act: eventType,
      player: this.player,
      annotationPlugin: this.annotationPlugin,
      projectId: this.projectId,
      sectionId: this.sectionId,
      subSectionId: this.subsectionId,
      typeId: this.typeId,
      itemId: this.item.id,
    };

    if (eventType === 'onVideoPaused') {
      onVideoPaused.emit(eventData);
      onVideoPlayerInitialized.emit({
        act: 'videoPlayerInitialized',
      });
    } else if (eventType === 'onVideoPlay') {
      onVideoPlay.emit(eventData);
    } else if (eventType === 'videoPlayerInitialized') {
      onVideoPlayerInitialized.emit(eventData);
    }
  }

  private setVideoSubtitles(): void {
    if (!this.player || !this.enableSubtitle) {
      return;
    }

    const subtitles = this.player.textTracks();

    if (!this.videoSubtitle) {
      if (subtitles.length) {
        this.player.removeRemoteTextTrack(subtitles[0]);
      }
    } else {
      this.player.removeRemoteTextTrack(subtitles[0]);
      // setTimeout(() => {
      //   HelperService.addSubtitleTrackToPlayer(this.player, this.videoSubtitle);
      // }, 0);
    }
  }

  private setDefaultVideoOptions(): void {
    const downloadControlId = this.getDownloadControlId();
    const subtitlePlugin = this.enableSubtitle && this.subtitle ? this.buildSubtitlePlugin() : undefined;
    const options = this.configService.buildVideoJsOptions({
      file: this.file,
      item: this.item,
      autoPlayVideo: this.autoPlayVideo,
      height: this.height,
      videoPreloadMethod: this.videoPreloadMethod,
      enableVideoAnnotation: this.enableVideoAnnotation,
      width: this.width,
      miniPlayer: this.miniPlayer,
      downloadControlId,
      downloadCallback: (controlId: string, quality?: string) => this.handleDownload(controlId, quality),
      subtitlePlugin,
    });

    if (options) {
      this.videoOptions = options;
    }
  }

  private getDownloadControlId(): string {
    if (this.file?.projectId && this.file?.sectionId && this.file?.subSectionId && this.file?.id) {
      return `video-js-${this.file.projectId}-${this.file.sectionId}-${this.file.subSectionId}-${this.file.id}`;
    }
    return '';
  }

  private buildSubtitlePlugin(): any {
    return {
      name: 'subtitleButton',
      file: this.subtitle.signedURL,
      caption: this.fileUtilsService.getFileDisplayName(this.subtitle.displayName || this.subtitle.name),
      downloadCallback: () => {
        const id = uuid4();
        onDownloadStart.emit();
        const downloadInfo = MTMFileDownload.fromFileRef(this.subtitle);
        downloadInfo.id = id;
        this.transferService.downloadFile(downloadInfo);
      },
    };
  }

  private handleDownload(controlId: string, quality?: string): void {
    const file = JSON.parse(JSON.stringify(this.file));

    if (quality) {
      file.displayName = this.file.displayName.split('.').slice(0, -1).join('.') + '.mp4';
      file.fileName = this.file.name.split('.').slice(0, -1).join('.') + '.mp4';
    }

    const button = document.getElementById(controlId);
    button?.classList.add('downloading');

    onDownloadStart.emit();

    const downloadParams = this.getDownloadParameters(quality);
    const url = (quality ? file.signedURL : this.file.signedURL) + (downloadParams || '');
    const downloadInfo = MTMFileDownload.fromFileRef(quality ? file : this.file);
    downloadInfo.id = controlId;
    downloadInfo.url = url;

    this.transferService.downloadFile(downloadInfo);
    this.videoDownload.emit({ url, quality });
  }

  private getDownloadParameters(quality?: string): string | null {
    return this.helperService.buildDownloadQuery(this.file, quality);
  }

  private checkMp4(filename: string): boolean {
    return filename && this.isFileExtension(filename, 'mp4');
  }

  generateSignature() {
    return this.rbacSignature
      .requestSignature('FILE', this.item.id, this.item.displayName)
      .pipe(catchError(() => of<SignatureRequestResult | null>(null)));
  }

  prepareParticipantList() {
    const inputParticipants = this.projectParticipants || [];
    const normalizedAuthEmail = (this.authUser?.email || '').toLowerCase();

    const hasAuthUser = inputParticipants.some((p) => {
      const email = (p?.email || p?.accountEmail || '').toLowerCase();
      return email && normalizedAuthEmail && email === normalizedAuthEmail;
    });

    const mapped = inputParticipants.map((participant) => ({
      accountEmail: participant?.email || participant?.accountEmail,
      owner: (participant?.email || participant?.accountEmail) === this.authUser?.email,
    }));

    if (!hasAuthUser && normalizedAuthEmail) {
      mapped.push({
        accountEmail: this.authUser.email,
        owner: true,
      });
    }

    return mapped;
  }

  public onPlaybackRateChange(playbackRate: number): void {
    this.currentPlaybackRate.set(playbackRate);
    const { player } = this;
    if (player?.playbackRate) {
      player.playbackRate(playbackRate);
    }
  }

  public onResolutionChange(resolution: { res: string; src: string; type: string }): void {
    this.currentResolution.set(resolution.res);
    const { player } = this;
    if (!player?.src) return;
    const currentTime: number = player.currentTime() ?? 0;
    const isPaused: boolean = player.paused();
    const playbackRate: number = player.playbackRate() ?? 1;
    player.pause();
    player.src([{ src: resolution.src, type: resolution.type, res: resolution.res }]);
    player.one('loadeddata', () => {
      player.currentTime(currentTime);
      player.playbackRate(playbackRate);
      if (!isPaused) {
        player.play();
      }
    });
  }

  get isAnnotationDisplayEnabled(): boolean {
    return this.isAnnotationModeActive;
  }

  toggleAnnotationDisplay(): void {
    if (!this.player) return;

    this.isAnnotationModeActive = !this.isAnnotationModeActive;

    // Toggle global class to player to toggle display of elements
    const playerEl = $(this.player.el());
    playerEl.toggleClass('vac-active');

    // Toggle canvas container
    const canvasContainer = playerEl.find('.vac-canvas');
    if (canvasContainer.length) {
      canvasContainer.toggleClass('vac-hidden');
    }

    // Toggle annotation bar for this instance only (scope to this viewer's container so compare mode works correctly)
    const playerParent = this.player.el().parentElement as HTMLElement | null;
    const annotationBarEl = playerParent?.querySelector('.annotation-bar-container');
    if (annotationBarEl) {
      $(annotationBarEl).toggleClass('vac-hidden').toggleClass('d-block');
    }

    // Since we've replaced the native toggle plugin, update the annotation plugin state
    if (this.annotationPlugin && this.pluginReady) {
      const plugin = this.annotationPlugin as any;
      if (plugin.annotationState) {
        plugin.annotationState.enabled = this.isAnnotationModeActive;
      }

      if (this.isAnnotationModeActive) {
        plugin.fire('annotationModeEnabled');
        if (plugin.options?.showControls && plugin.controls?.render) {
          plugin.controls.render();
        }
      } else {
        plugin.fire('annotationModeDisabled');
        if (plugin.controls) {
          if (plugin.controls.cancelAddNew) plugin.controls.cancelAddNew();
          if (plugin.options?.showControls && plugin.controls.clear) {
            plugin.controls.clear();
          }
        }
      }
    }
  }

  toggleFullscreenForViewer(): void {
    if (!this.player) return;

    if (this.player.isFullscreen()) {
      this.player.exitFullscreen();
    } else {
      if (this.subsectionService.uiState.cinemaMode) {
        this.subsectionService.updateUIState({ cinemaMode: false, explorerCollapsed: false });
      }
      this.player.requestFullscreen();
    }
  }

  togglePlay(): void {
    if (!this.player) return;
    if (this.player.paused()) {
      this.player.play();
    } else {
      this.player.pause();
    }
  }

  onVolumeChange(vol: number): void {
    if (!this.player) return;
    this.player.volume(vol);
    if (vol > 0 && this.player.muted()) {
      this.player.muted(false);
    }
  }

  toggleMute(): void {
    if (!this.player) return;
    this.player.muted(!this.player.muted());
  }

  public onTimeDisplayFormatChange(format: 'Seconds' | 'Frames'): void {
    this.timeDisplayFormat = format;
  }

  private setupProgressBarTooltipFormat(): void {
    // VJS 8: mouseTimeDisplay and timeTooltip are only accessible via getChild().
    // Patch timeTooltip.update(rect, point, content) — the final DOM setter called
    // after updateTime() formats the time string — so we can replace with frame count.
    // We patch both tooltips:
    //   1. mouseTimeDisplay → timeTooltip: hover cursor position
    //   2. playProgressBar → timeTooltip: current playback position
    type VjsTimeTooltip = {
      update: (rect: DOMRectReadOnly, point: number, content: string) => void;
    };
    type VjsComponent = {
      getChild: (name: string) => VjsComponent | undefined;
    };

    const seekBar: VjsComponent | undefined = this.player?.controlBar?.progressControl?.seekBar as
      | VjsComponent
      | undefined;

    const patchTooltip: (tooltip: VjsTimeTooltip) => void = (tooltip: VjsTimeTooltip): void => {
      const origUpdate: (rect: DOMRectReadOnly, point: number, content: string) => void = tooltip.update.bind(tooltip);
      tooltip.update = (seekBarRect: DOMRectReadOnly, seekBarPoint: number, content: string): void => {
        if (this.timeDisplayFormat === 'Frames') {
          const time: number = seekBarPoint * (this.player.duration() || 0);
          const frame: number = Math.floor(time * (this.frameRate || 25));
          origUpdate(seekBarRect, seekBarPoint, frame.toString());
        } else {
          origUpdate(seekBarRect, seekBarPoint, content);
        }
      };
    };

    const mtd: VjsComponent | undefined = seekBar?.getChild('mouseTimeDisplay');
    const hoverTooltip: VjsTimeTooltip | undefined = mtd?.getChild('timeTooltip') as unknown as
      | VjsTimeTooltip
      | undefined;
    if (hoverTooltip?.update) {
      patchTooltip(hoverTooltip);
    }

    const ppb: VjsComponent | undefined = seekBar?.getChild('playProgressBar');
    const currentTimeTooltip: VjsTimeTooltip | undefined = ppb?.getChild('timeTooltip') as unknown as
      | VjsTimeTooltip
      | undefined;
    if (currentTimeTooltip?.update) {
      patchTooltip(currentTimeTooltip);
    }
  }
}
